import { ArrowsClockwise, CheckCircle, LockSimple, Play, X } from '@phosphor-icons/react';
import type { Song } from '../data/mockCatalog';
import type { Difficulty, PreparationStatus } from '../store/drumAppAtoms';

const LEVELS: Array<{ id: Difficulty; color: string; formatKo: string; formatEn: string }> = [
  { id: 'Easy', color: '#35bd75', formatKo: '컬러드럼악보', formatEn: 'COLOR DRUM' },
  { id: 'Normal', color: '#f2b928', formatKo: '컬러오선악보', formatEn: 'COLOR STAFF' },
  { id: 'Hard', color: '#ef4f61', formatKo: '오선악보', formatEn: 'CLASSIC STAFF' },
];

const ERROR_COPY: Record<string, Record<string, [string, string]>> = {
  ko: {
    'audio-error': ['음원을 불러오지 못했습니다', '네트워크 상태를 확인한 뒤 다시 시도해 주세요.'],
    'abc-error': ['악보를 표시할 수 없습니다', '등록된 악보 정보를 확인할 수 없습니다.'],
    offline: ['인터넷 연결이 필요합니다', '인터넷에 연결한 뒤 다시 시도해 주세요.'],
    'update-required': ['새 악보를 불러와야 합니다', '최신 악보를 다시 불러와 주세요.'],
  },
  en: {
    'audio-error': ['Unable to load audio', 'Check your connection and try again.'],
    'abc-error': ['Unable to display the score', 'The registered score could not be loaded.'],
    offline: ['Internet connection required', 'Connect to the internet and try again.'],
    'update-required': ['A score update is required', 'Load the latest score and try again.'],
  },
};

function ScorePreview({ level }: { level: Difficulty }) {
  if (level === 'Easy') {
    return (
      <div className="stage-score-preview stage-score-preview--dots" aria-hidden="true">
        <i className="red" /><i className="blue" /><i className="pink" /><b /><i className="orange" /><i className="purple" /><i className="rest" />
      </div>
    );
  }
  const colors = level === 'Hard' ? ['#242a32', '#242a32', '#242a32', '#242a32', '#242a32'] : ['#ef4455', '#3185ee', '#ef55a5', '#35b85f', '#ff8a22'];
  return (
    <div className={`stage-score-preview stage-score-preview--staff ${level === 'Hard' ? 'classic' : 'colored'}`} aria-hidden="true">
      <span className="stage-staff-lines">{[0, 1, 2, 3, 4].map((line) => <i key={line} />)}</span>
      {colors.map((color, index) => (
        <b key={`${color}-${index}`} style={{ '--note-color': color, left: `${12 + index * 18}%`, top: `${24 + (index % 3) * 15}%` } as React.CSSProperties} />
      ))}
    </div>
  );
}

interface StagePreparationProps {
  song: Song | null | undefined;
  difficulty?: Difficulty;
  language?: string;
  status?: PreparationStatus;
  hasDrumPass?: boolean;
  onDifficulty: (difficulty: Difficulty) => void;
  onReady: (difficulty: Difficulty) => void;
  onRetry: () => void;
  onBack: () => void;
}

export default function StagePreparation({ song, difficulty = 'Easy', language = 'ko', status = 'loading', hasDrumPass = false, onDifficulty, onReady, onRetry, onBack }: StagePreparationProps) {
  const isLoading = status === 'loading';
  const isReady = status === 'ready';
  const error = ERROR_COPY[language]?.[status] || ERROR_COPY.en[status];
  const selectedScore = song?.difficulties?.[difficulty];
  const selectedLocked = !hasDrumPass && !selectedScore?.freeAccess;

  return (
    <section className={`stage-preparation stage-preparation--${status}`} aria-live={error ? 'assertive' : 'polite'} aria-busy={isLoading}>
      <button className="stage-preparation__close" type="button" onClick={onBack} aria-label={language === 'ko' ? '곡 선택으로 돌아가기' : 'Return to song selection'}><X weight="bold" /></button>
      <div className="stage-preparation__song">
        <h1>{song?.title || '-'}</h1>
        <p>{song?.artist || '-'}</p>
      </div>

      {isLoading && (
        <div className="stage-preparation__loading" role="progressbar" aria-label={language === 'ko' ? '악보와 음원 불러오는 중' : 'Loading score and audio'}>
          <strong>{language === 'ko' ? '악보와 음원을 불러오는 중' : 'LOADING SCORE & AUDIO'}</strong>
          <small>{language === 'ko' ? '연주 준비가 완료되면 난이도를 선택할 수 있습니다.' : 'Difficulty selection will appear when preparation is complete.'}</small>
          <span><i /></span>
        </div>
      )}

      {isReady && (
        <div className="stage-preparation__ready">
          <div className="stage-preparation__levels">
            {LEVELS.map((level) => {
              const score = song?.difficulties?.[level.id];
              const locked = !hasDrumPass && !score?.freeAccess;
              const selected = difficulty === level.id;
              return (
                <button type="button" key={level.id} aria-pressed={selected} className={`stage-level ${selected ? 'active' : ''} ${locked ? 'locked' : ''}`} style={{ '--level-color': level.color } as React.CSSProperties} onClick={() => onDifficulty(level.id)}>
                  <span><strong>{level.id.toUpperCase()}</strong><small>{language === 'ko' ? level.formatKo : level.formatEn}</small></span>
                  {selected ? (
                    <b className="stage-level__selected">{locked ? <LockSimple weight="fill" /> : <CheckCircle weight="fill" />}{language === 'ko' ? (locked ? '선택됨 · 잠금' : '선택됨') : (locked ? 'SELECTED · LOCKED' : 'SELECTED')}</b>
                  ) : locked && <em><LockSimple weight="fill" />{language === 'ko' ? '잠금' : 'LOCKED'}</em>}
                  <ScorePreview level={level.id} />
                </button>
              );
            })}
          </div>
          <div className="stage-preparation__actions">
            <button className={`stage-preparation__button stage-preparation__button--primary ${selectedLocked ? 'locked' : ''}`} type="button" onClick={() => onReady(difficulty)}>
              {selectedLocked ? <LockSimple weight="fill" /> : <Play weight="fill" />}
              {selectedLocked ? (language === 'ko' ? '잠금 해제 후 연주' : 'UNLOCK & PLAY') : (language === 'ko' ? '드럼 연주 시작' : 'START')}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="stage-preparation__error" role="alert">
          <h1>{error[0]}</h1><p>{error[1]}</p>
          <div className="stage-preparation__actions">
            <button className="stage-preparation__button stage-preparation__button--primary" type="button" onClick={onRetry}><ArrowsClockwise />{language === 'ko' ? '다시 시도' : 'TRY AGAIN'}</button>
          </div>
        </div>
      )}
    </section>
  );
}
